import { z } from "zod";
import { Timestamps } from "../../common/index.ts";
import { EntityDto } from "../entities/entities.schema.ts";
import {
  DocumentTypeId,
  EntityId,
  FsmTranslationsSchema,
  StateId,
  CustomerId,
  TransitionId,
} from "../fsm.schema.ts";
import { StateDto } from "../states/states.schema.ts";
import { MaterialShippedQuantityEffect } from "../../materials/materials-shipping.schema.ts";

/**
 * @description A Transition represents a possible change from one State to another for a DocumentType within the FSM.
 */
export const TransitionType = z.enum(["admin_edit"]);
export type TransitionType = z.infer<typeof TransitionType>;

export const TransitionDto = z
  .object({
    id: TransitionId,
    customerId: CustomerId,
    documentTypeId: DocumentTypeId,
    name: z.string().min(1),
    // BEL-651: stable identifier, independent of the editable Spanish name.
    // Nullable — matches the DB column; a customer-authored transition
    // created via the admin UI (no key input there) legitimately has none.
    key: z.string().nullable(),
    fromStateId: StateId,
    toStateId: StateId,
    executorEntityIds: z.array(EntityId),
    transitionType: TransitionType.nullable().optional(),
    triggerDocumentTypeId: DocumentTypeId.nullish(),
    sourceHandleId: z.string().nullish(),
    targetHandleId: z.string().nullish(),
    translations: FsmTranslationsSchema.nullable(),
    shippedQuantityEffect: MaterialShippedQuantityEffect.nullish(),
  })
  .merge(Timestamps);

export type TransitionDto = z.infer<typeof TransitionDto>;

export const TransitionHydrated = TransitionDto.extend({
  fromState: StateDto,
  toState: StateDto,
  executorEntities: z.array(EntityDto),
});

export type TransitionHydrated = z.infer<typeof TransitionHydrated>;

export const TransitionErrorCode = z.enum([
  "INVALID_TRANSITION",
  "UNAUTHORIZED",
  "GUARD_FAILED",
]);

export type TransitionErrorCode = z.infer<typeof TransitionErrorCode>;

export const CreateTransitionInput = z.object({
  name: z.string().min(1),
  documentTypeId: DocumentTypeId,
  fromStateId: StateId,
  toStateId: StateId,
  executorEntityIds: z.array(EntityId),
  transitionType: TransitionType.nullable().optional(),
  triggerDocumentTypeId: DocumentTypeId.optional(),
  sourceHandleId: z.string().optional(),
  targetHandleId: z.string().optional(),
  translations: FsmTranslationsSchema.optional(),
});
export type CreateTransitionInput = z.infer<typeof CreateTransitionInput>;

export const UpdateTransitionInput = z.object({
  name: z.string().min(1).optional(),
  fromStateId: StateId.optional(),
  toStateId: StateId.optional(),
  executorEntityIds: z.array(EntityId).optional(),
  triggerDocumentTypeId: DocumentTypeId.nullable().optional(),
  sourceHandleId: z.string().optional(),
  targetHandleId: z.string().optional(),
  translations: FsmTranslationsSchema.nullable().optional(),
});
export type UpdateTransitionInput = z.infer<typeof UpdateTransitionInput>;
